import {
  BadgePercent,
  CalendarCheck,
  Coins,
  Eye,
  Lock,
  Play,
  ShieldCheck,
  Sparkles,
  UserPlus,
  Zap,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { useCan } from "@/lib/auth";

/**
 * The "How to use" walkthrough: the admin setup + monthly process, with
 * behind-the-scenes notes so users understand what the system does for them.
 * Static prose — the process reference lives with the feature it documents,
 * mirroring the reconciliation steps' human-facing content.
 */
export function HowToView() {
  const isAdmin = useCan("commissions.admin");
  return (
    <div className="flex max-w-3xl flex-col gap-4">
      <Card className="p-5">
        <h2 className="text-lg font-semibold">What this section is</h2>
        <p className="mt-1.5 text-sm text-muted-foreground">
          The accounting ledger for sales commissions. For every contract it
          records who gets paid, at what rate, for which months — and each month
          it computes the actual payouts from <em>paid invoices</em> (cash-on-cash)
          and locks them permanently at reconciliation step R-12. If you have
          read access, you see only your own commissions; admins see everyone's.
        </p>
      </Card>

      <SectionCard
        icon={<UserPlus className="h-4 w-4" />}
        step="Setup 1"
        title="Add the payees (Admin → Payees)"
        adminOnly
        isAdmin={isAdmin}
        points={[
          {
            text: "Employees are picked from the Cinch Ops user list — known closers sort to the top. You can't type an employee name by hand.",
            behind:
              "The user link is what powers privacy: when that person logs in with read access, every commissions view filters to payee rows linked to their login. No link, no visibility.",
          },
          {
            text: "Referral partners (non-employees) flip the 'Referral partner' switch and get an ad hoc name — they have no login.",
            behind:
              "Partners are contracted counterparties; until they become first-class records in Cinch billing, this roster is their system of record.",
          },
          {
            text: "Deactivate a payee instead of deleting once they have history — deletion is blocked when assignments or payouts reference them.",
            behind:
              "Locked months must stay reproducible forever, so anything referenced by history is protected from deletion.",
          },
        ]}
      />

      <SectionCard
        icon={<BadgePercent className="h-4 w-4" />}
        step="Setup 2"
        title="Set the comp plan (Admin → Comp Plan)"
        adminOnly
        isAdmin={isAdmin}
        points={[
          {
            text: "The comp plan is one NAMED, dated document — the whole role × contract-type grid (e.g. '2026 Comp Plan', effective Jan 1). Create a new plan (usually copying the current one), adjust its cells, and it waits for CFO/CEO approval as a unit.",
            behind:
              "Approval is what puts a plan into effect on its date and closes the previous plan the day before. Past plans stay reviewable forever from the plan picker. Editing an approved plan's grid resets it to pending — nothing changes without a fresh approval. Assignments SNAPSHOT the plan rate when they're created — a new plan never rewrites an existing deal.",
          },
          {
            text: "A deal rate that differs from the plan is 'off-plan' and lands in Admin → Approvals; it does not pay until approved.",
            behind:
              "Approval records who approved and when — the audit trail for kickers and negotiated rates.",
          },
        ]}
      />

      <SectionCard
        icon={<Sparkles className="h-4 w-4" />}
        step="Setup 3"
        title="Work the backlog (Contracts)"
        adminOnly
        isAdmin={isAdmin}
        points={[
          {
            text: "The Contracts tab lists every contract still inside its payout window, newest sign date first, with a Coverage chip showing whether anybody is assigned. Start here — you should never have to guess which contract to look up.",
            behind:
              "Live = not cancelled, still billing, and first billed within the last 13 months (M1–M12 plus the P1 stub). Anything older can no longer owe a commission. 'Missing a paid role' means somebody IS assigned but the comp plan prices a role nobody holds — the state that quietly loses a person their commission.",
          },
          {
            text: "Click Assign and the dialog opens pre-filled: the comp plan decides which roles get paid on that contract type, and HubSpot decides who those people are. Every row shows how its payee was matched, so a solid hit reads differently from a guess.",
            behind:
              "The closer is resolved through the HubSpot deal owner's EMAIL first, then the contract's HubSpot owner id, then the closer's name. Email matters because closer_hubspot_owner_id is populated on only about 10% of contracts — going through the people directory instead lifts resolution to roughly three quarters of the roster. Standing seats (CRO, VP Sales) fill from the payee roster when exactly one active payee holds that role; two candidates and it asks rather than guessing.",
          },
          {
            text: "The contract's dates — sign date, first billing, M1, the P1 call, item scope — are entered ONCE at the top and applied to every payee. Only the person, role, rate and month window vary per row. Add as many people as the deal pays and save them in one go.",
            behind:
              "The whole slate is validated before anything is written, so a bad rate on the fourth payee rejects the submission instead of leaving the first three saved. Rates that differ from the plan are flagged off-plan and land as pending approval.",
          },
          {
            text: "Anybody HubSpot names whom the plan does not price — a BDR, a referral partner — appears under 'HubSpot found people the plan doesn't price', with a one-click button to add them off-plan.",
            behind:
              "Because the slate is plan-driven, a gap in the comp-plan grid would otherwise drop that person silently. Surfacing them is the safeguard. If the plan can't price the contract at all (no approved plan, or an unmapped contract type) the dialog says so instead of coming back mysteriously empty.",
          },
        ]}
      />

      <SectionCard
        icon={<Coins className="h-4 w-4" />}
        step="Setup 4"
        title="Assign payees to contracts (Contract Ledger)"
        adminOnly
        isAdmin={isAdmin}
        points={[
          {
            text: "Load a contract by its ID, click 'Add payee', and the dialog pre-fills company, contract type, closer, sign date, and first billing date from the warehouse.",
            behind:
              "The pre-fill reads the contract roster in Snowflake. The P1 ('13th month') eligibility is computed live from the 7-day sign-to-first-billing rule — you'll see the day count and can override it.",
          },
          {
            text: "A contract can pay several people — AE, referral partner, CRO override, SE — each with their own rate and window. Stepped rates (e.g. 4% months 1–3, 8% after) are separate rows with disjoint month ranges.",
            behind:
              "Overlapping month ranges for the same payee are rejected so a step boundary can never double-pay.",
          },
          {
            text: "Scope an assignment to specific items ('Reputation Management only') when the deal terms carve out products.",
            behind:
              "Replacement contracts use the replacement-delta basis automatically: commission pays only on the increase over the greater of prior-month billing or the replaced contract's MRR.",
          },
        ]}
      />

      <SectionCard
        icon={<Play className="h-4 w-4" />}
        step="Monthly 1"
        title="Run the month (Monthly Report)"
        adminOnly
        isAdmin={isAdmin}
        points={[
          {
            text: "Pick the month that just closed and click 'Run month'. Recompute as often as you like while it's a draft.",
            behind:
              "The run pulls every subscription + discount invoice line for the month from the QuickBooks general ledger, checks each invoice's paid status, matches lines to assignments by customer and item scope, applies the right rate for that window month, and writes a payout line per item. Nothing is paid on an unpaid invoice — those lines show as Held.",
          },
          {
            text: "Review each payee's row — expand it for the per-company breakdown with subtotals and the invoice-line evidence.",
            behind:
              "Catch-up lines appear automatically when a previously held invoice has since been paid: the rep gets the held month AND the current month together, labeled with the month each payment covers.",
          },
        ]}
      />

      <SectionCard
        icon={<Eye className="h-4 w-4" />}
        step="Monthly 2"
        title="Chase what's held (Collections)"
        isAdmin={isAdmin}
        points={[
          {
            text: "Collections lists every commission held on an unpaid invoice — who's owed, which customer, which invoice, how long it's been held. Reps see their own; admins see all.",
            behind:
              "This is deliberate: the person with the commission at stake is the person most motivated to help collect. When cash arrives, the next run pays it as a catch-up — no manual fixing.",
          },
        ]}
      />

      <SectionCard
        icon={<CalendarCheck className="h-4 w-4" />}
        step="Monthly 3"
        title="Reconcile quota & kickers (recon step R-12)"
        adminOnly
        isAdmin={isAdmin}
        points={[
          {
            text: "In the monthly reconciliation, step R-12 (owned by CRO, RevOps, Finance) is where quota attainment is reviewed and any kicker rate changes are applied — as per-contract rate edits, which route through off-plan approval.",
            behind:
              "Kickers apply prospectively (deals closed after crossing quota). Kicker automation is planned; for now the rate on each affected deal is the record.",
          },
        ]}
      />

      <SectionCard
        icon={<Coins className="h-4 w-4" />}
        step="Monthly 4"
        title="Record what was actually paid (Paid Amounts)"
        adminOnly
        isAdmin={isAdmin}
        points={[
          {
            text: "After payroll runs, enter each payee's actual total paid for the month with a note for any variance — this is the bridge to the financials.",
            behind:
              "The tab shows the system's computed payable next to your entry and computes the variance live. It's gated to admins and the dedicated commissions.write grant (e.g. the controller), so the person booking payments can record without full admin access.",
          },
        ]}
      />

      <SectionCard
        icon={<Lock className="h-4 w-4" />}
        step="Monthly 5"
        title="Lock the month"
        adminOnly
        isAdmin={isAdmin}
        points={[
          {
            text: "When the report matches what will actually be paid, click 'Lock month'. Locked months are permanent history.",
            behind:
              "A locked month can never be recomputed or edited — if a payment lands later, it becomes a catch-up line in the NEXT open month. That's what makes every historical payout exactly reproducible, forever.",
          },
        ]}
      />

      <Card className="p-5">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-power" />
          <h3 className="font-semibold">Who sees what</h3>
        </div>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
          <li>
            <span className="font-medium text-foreground">Read access</span> — your own
            commissions only: your report rows, your contract ledgers, your held
            invoices. Other payees' names and amounts never load.
          </li>
          <li>
            <span className="font-medium text-foreground">Admin access</span> — everything,
            plus payee/plan management, off-plan approvals, and run/lock. R-12 owners
            (CRO, RevOps, Finance) can also run and lock months.
          </li>
        </ul>
      </Card>

      <Card className="p-5">
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4 text-power" />
          <h3 className="font-semibold">Not built yet (known, planned)</h3>
        </div>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
          <li>Workbook history backfill (Dec 2021 → cutover) into locked months.</li>
          <li>Low-margin rate reduction (the proposals margin flag, contracts-side).</li>
          <li>Mailer profit basis wired to COS-Units costs (flagged lines compute at $0 with a warning until then).</li>
          <li>One-time implementation-fee commissions; kicker automation; clawbacks for mid-term churn.</li>
        </ul>
      </Card>

      {!isAdmin && (
        <p className="text-xs text-muted-foreground">
          Sections marked <Badge variant="secondary">admin</Badge> describe what your
          admin does — included so you know where your numbers come from.
        </p>
      )}
    </div>
  );
}

function SectionCard({
  icon,
  step,
  title,
  points,
  adminOnly = false,
  isAdmin,
}: {
  icon: React.ReactNode;
  step: string;
  title: string;
  points: { text: string; behind: string }[];
  adminOnly?: boolean;
  isAdmin: boolean;
}) {
  return (
    <Card className="p-5">
      <div className="flex items-center gap-2">
        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-power-light text-power">
          {icon}
        </div>
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {step}
        </span>
        <h3 className="font-semibold">{title}</h3>
        {adminOnly && !isAdmin && <Badge variant="secondary">admin</Badge>}
      </div>
      <ul className="mt-3 space-y-3">
        {points.map((p, i) => (
          <li key={i} className="text-sm">
            <p>{p.text}</p>
            <p className="mt-1 rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
              <span className="font-semibold">Behind the scenes:</span> {p.behind}
            </p>
          </li>
        ))}
      </ul>
    </Card>
  );
}
